<template>
  <nav :aria-label="$t.utilityNavigationLabel" class="text-sm lh-3">
    <client-only>
      <vf-link
        v-if="$feature.showSignInLinkMobileOnTop && !auth.loggedIn"
        class="lg:hidden"
        role="button"
        to="/sign-in"
        variant="quiet"
        @click.prevent.capture="handleSignIn"
      >
        {{ $t.signIn }}
      </vf-link>
    </client-only>
    <vf-location-selector @click="handleClick($t.locationSelector)" />
    <template v-for="({ type, to, title }, key) in links" :key>
      <base-button
        v-if="type === 'chat' && $feature.enableChat"
        class="hover:underlined"
        data-chat="utility-nav"
        @click="handleChat(title)"
      >
        {{ title }}
      </base-button>
      <template v-else-if="type === 'loyalty' && $feature.enableLoyaltyUtilityNav">
        <vf-link
          to="/loyalty"
          variant="quiet"
          @click="handleClick($t.loyaltyUtilityNavLabel)"
        >
          {{ $t.loyaltyUtilityNavLabel }}
        </vf-link>
      </template>
      <vf-link v-else-if="type !== 'chat' && type !== 'loyalty'" :to variant="quiet" @click="handleClick(title)">
        {{ title }}
      </vf-link>
    </template>
    <vf-link
      v-if="giftCardLocales.includes($locale)"
      to="/gift-card-page"
      variant="quiet"
      @click="handleClick($t.giftCardUtilityNavigation)"
    >
      {{ $t.giftCardUtilityNavigation }}
    </vf-link>
    <vf-link
      v-if="newsletterLocales.includes($locale)"
      :to="{
        path: '/newsletter-sign-up',
        state: { formLocation: 'inline:body:header:none' },
      }"
      variant="quiet"
      @click="handleClick($t.newsletter)"
    >
      {{ $t.newsletter }}
    </vf-link>
    <vf-link
      v-if="$feature.allowFavorites"
      class="flex center gap-1"
      to="/favorites"
      variant="quiet"
      @click="handleClick($t.favorites)"
    >
      {{ $t.favorites }}
      <span
        v-if="favoritesStore.favorites?.length"
        class="pointer-events-none h-4 w-4 flex select-none center b-2 b-white rounded-full bg-brand-2 c-white"
        style="font-size: 0.5rem"
      >
        {{ favoritesStore.favoritesCount }}
      </span>
    </vf-link>
    <client-only>
      <template v-if="auth.loggedIn">
        <vf-link to="/account" variant="quiet" @click="handleClick($t.myAccount)">
          {{ $t.myAccount }}
        </vf-link>
        <base-button class="hover:underlined" @click="handleSignOut">
          {{ $t.signOut }}
        </base-button>
      </template>
      <template v-else>
        <vf-link
          :class="{ '<lg:hidden': $feature.showSignInLinkMobileOnTop }"
          role="button"
          to="/sign-in"
          variant="quiet"
          @click.prevent.capture="handleSignIn"
        >
          {{ $t.signIn }}
        </vf-link>
      </template>
    </client-only>
  </nav>
</template>

<script lang="ts" setup>
import type { LocaleCode } from '#types/locale'
import type { NavigationLink } from '#types/navigation'

const { links } = defineProps<{
  links: NavigationLink[]
}>()

const emit = defineEmits<{
  /**
   * Emits when a navigation item is clicked
   */
  click: []
}>()

const auth = useAuthStore()
const { DialogSignIn } = useDialogsStore()
const favoritesStore = useFavoritesStore()

const { $chat, $gtm, $t } = useNuxtApp()
const { giftCardLocales } = useAppConfig().components.vf.utilityNavigation

const newsletterLocales: LocaleCode[] = [
  'cs-cz',
  'da-dk',
  'de-at',
  'de-ch',
  'de-lu',
  'en-dk',
  'en-fi',
  'en-lu',
  'en-se',
  'fr-be',
  'fr-ch',
  'it-ch',
  'nl-be',
  'pl-pl',
  'pt-pt',
  'sv-fi',
  'sv-se',
]

const handleClick = (title: string) => {
  emit('click')
  $gtm.push('topNav.onClickUtilityLink', capitalize(title))
}

const handleChat = (title: string) => {
  handleClick(title)
  $chat.open()
}

const handleSignIn = async () => {
  handleClick($t.signIn)
  DialogSignIn.open({
    formLocation: 'modal:single:header:none'
  })
}

const handleSignOut = () => {
  handleClick($t.signOut)
  auth.logout()
}
</script>
